import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  PublicUser,
  RegisterOfficerRequest,
  RegisterOfficerResponse,
  RegistrationSummary,
  UpdateProfileRequest,
} from '@cosvision/shared';
import {
  approveRegistration,
  listRegistrations,
  registerOfficer,
  rejectRegistration,
  updateMyProfile,
} from '../lib/api/auth.js';
import { qk } from '../lib/query-keys.js';
import { useAuthStore } from '../store/auth-store.js';

export function useUpdateProfileMutation(): UseMutationResult<
  PublicUser,
  unknown,
  UpdateProfileRequest
> {
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (patch: UpdateProfileRequest) => updateMyProfile(patch),
    onSuccess: (user) => {
      setUser(user);
    },
  });
}

export function useRegisterMutation(): UseMutationResult<
  RegisterOfficerResponse,
  Error,
  RegisterOfficerRequest
> {
  return useMutation({
    mutationFn: (body: RegisterOfficerRequest) => registerOfficer(body),
  });
}

/** CO-only: officers awaiting approval. Polls so the badge stays current. */
export function useRegistrationsQuery(enabled = true): UseQueryResult<RegistrationSummary[]> {
  return useQuery({
    queryKey: qk.auth.registrations(),
    queryFn: () => listRegistrations(),
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useApproveRegistrationMutation(): UseMutationResult<
  RegistrationSummary,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveRegistration(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.auth.registrations() });
      void qc.invalidateQueries({ queryKey: qk.people.all });
    },
  });
}

export function useRejectRegistrationMutation(): UseMutationResult<
  RegistrationSummary,
  Error,
  { id: string; reason?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => rejectRegistration(id, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.auth.registrations() });
    },
  });
}
